import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  ExclamationTriangleIcon,
  ListIcon,
  RotateCwIcon,
  SparklesIcon,
  XMarkIcon,
} from '@/components/Icons'
import { SETTINGS_BACKLOG_PATH } from '@/constants/settings-tabs'
import { fmt } from '@/constants/ui-locale'
import { useMeetingBrief } from '@/hooks/useMeetingBrief'
import { useNow } from '@/hooks/useNow'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchProjects } from '@/services/projects'
import {
  getPreviousMeetingDate,
  suggestProject,
  type Meeting,
  type Project,
} from '@/services/meetingBrief'
import { formatDate, formatTime } from '@/utils/formatDateTime'
import BriefContent from './BriefContent'
import BriefLoadingState from './BriefLoadingState'
import ProjectDropdown from './ProjectDropdown'
import { formatCountdown, formatMeetingWindow } from './briefFormat'

interface BriefModalProps {
  meeting: Meeting
  /**
   * The project this meeting was last generated with, if any.
   *
   * A reopened modal refreshes that project instead of dropping the reader back
   * to the empty state — "always check the latest" from the brief belongs to
   * the data, not to the modal's lifetime.
   */
  initialProject: Project | null
  onProjectChange: (project: Project) => void
  onClose: () => void
}

/**
 * The meeting brief, in the three states the feature is defined by: nothing
 * chosen, generating, and ready.
 *
 * Portalled because the page behind it is an ordinary scrolling document, and
 * because a transformed ancestor would otherwise turn `fixed` into something
 * that scrolls with the table.
 */
export default function BriefModal({
  meeting,
  initialProject,
  onProjectChange,
  onClose,
}: BriefModalProps) {
  const { t, locale } = useUiStrings()
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const [dialogElement, setDialogElement] = useState<HTMLDivElement | null>(null)
  /**
   * The dialog element is also the portal target for the floating project
   * panel. Keeping the panel inside the dialog (rather than on `document.body`)
   * means `aria-modal` and the focus trap still cover it.
   */
  const setDialogRef = useCallback((element: HTMLDivElement | null) => {
    dialogRef.current = element
    setDialogElement((current) => (current === element ? current : element))
  }, [])
  const now = useNow(30_000)
  const [projects, setProjects] = useState<Project[]>([])
  const [projectLoad, setProjectLoad] = useState<
    'loading' | 'ready' | 'empty' | 'error' | 'connect' | 'reauth'
  >('loading')
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null)
  const [projectLoadNonce, setProjectLoadNonce] = useState(0)
  useEffect(() => {
    let cancelled = false
    setProjectLoad('loading')
    setProjectLoadError(null)
    void fetchProjects()
      .then((response) => {
        if (cancelled) return
        setProjects(
          response.projects.map((item) => ({
            projectKey: item.projectKey,
            name: item.name,
          }))
        )
        if (response.status === 'CONNECTION_REQUIRED') {
          setProjectLoad('connect')
          return
        }
        if (response.status === 'REAUTH_REQUIRED') {
          setProjectLoad('reauth')
          return
        }
        setProjectLoad(response.projects.length > 0 ? 'ready' : 'empty')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setProjects([])
        setProjectLoadError(error instanceof Error ? error.message : null)
        setProjectLoad('error')
      })
    return () => {
      cancelled = true
    }
  }, [projectLoadNonce])
  const suggested = useMemo(() => suggestProject(meeting.title, projects), [meeting.title, projects])
  const { project, status, data, startedAt, generate, refresh } = useMeetingBrief(
    meeting,
    initialProject,
    projects
  )

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  useEffect(() => {
    /**
     * Escape closes; Tab stays inside.
     *
     * The portal lives outside `#root`, and `#root` is made inert below, so the
     * page behind the dialog cannot be reached by tabbing — but without this
     * wrap, Tab would still leave for the browser's own chrome at the last
     * control instead of returning to the first.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const dialog = dialogRef.current
      if (!dialog) return
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    // The page behind is visually covered; `inert` makes that true for assistive
    // technology and for Tab as well. The portal is a child of `body`, not of
    // `#root`, so the dialog itself stays interactive.
    const root = document.getElementById('root')
    if (!root) return undefined
    root.setAttribute('inert', '')
    return () => root.removeAttribute('inert')
  }, [])

  function chooseProject(nextProject: Project) {
    onProjectChange(nextProject)
    generate(nextProject)
  }

  function joinMeeting() {
    // Same new-tab contract as the table: joining opens Teams, it does not
    // start a recording here.
    window.open(meeting.joinUrl, '_blank', 'noopener,noreferrer')
  }

  const joinLabel = meeting.provider === 'google' ? t.googleCalendarJoin : t.teamsCalendarJoin
  const previousDateLabel = formatDate(getPreviousMeetingDate(meeting), locale)

  const aiStatus =
    status === 'loading' && !project
      ? t.briefAiPreparing
      : status === 'loading' && project
      ? fmt(t.briefPreparing, { project: project.name })
      : status === 'ready' && data
        ? fmt(t.briefAiGenerated, {
            time: formatTime(data.generatedAt, locale),
            date: formatDate(data.previousMeeting.heldAt, locale),
          })
        : status === 'error'
          ? t.briefAiFailed
          : status === 'skipped'
            ? t.briefAiSkipped
            : t.briefAiEmpty

  const aiStatusTone =
    status === 'error' ? 'text-rose-600 dark:text-rose-400' : 'text-[var(--accent)]'

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 p-0 backdrop-blur-sm motion-safe:animate-fade-in sm:items-center sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      <div
        ref={setDialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="brief-modal"
        className="motion-safe:animate-slide-up sm:motion-safe:animate-scale-in relative flex max-h-[100dvh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl sm:max-h-[90dvh] sm:rounded-2xl"
      >
        <header className="relative shrink-0 border-b border-[var(--border-color)] px-5 pb-4 pt-5 sm:px-6">
          <div className="flex items-start gap-3.5 pr-8">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
              <ListIcon className="h-5 w-5" />
            </div>

            <div className="min-w-0 space-y-1">
              <h2
                id={titleId}
                className="break-words text-base font-bold leading-snug text-[var(--text-primary)] sm:text-lg"
              >
                {fmt(t.briefModalTitle, { meeting: meeting.title })}
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                {formatMeetingWindow(meeting.startsAt, meeting.endsAt, locale)} ·{' '}
                <span>{formatCountdown(meeting.startsAt, now, t)}</span>
              </p>

              <div
                role="status"
                className={`flex items-center gap-1.5 pt-0.5 text-[11px] font-medium ${aiStatusTone}`}
              >
                {status === 'error' ? (
                  <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <SparklesIcon
                    className={`h-3.5 w-3.5 shrink-0 ${
                      status === 'loading' ? 'motion-safe:animate-pulse' : ''
                    }`}
                  />
                )}
                <span>{aiStatus}</span>
              </div>
            </div>
          </div>

          <button
            ref={closeRef}
            type="button"
            aria-label={t.briefCloseDialog}
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <section className="space-y-1.5">
            <ProjectDropdown
              projects={projects}
              selected={project}
              suggested={project ? null : suggested}
              onSelect={chooseProject}
              disabled={projectLoad !== 'ready'}
              portalTarget={dialogElement}
            />

            {projectLoad === 'loading' && (
              <p className="text-[11px] text-[var(--text-tertiary)]">{t.projectLoading}</p>
            )}
            {projectLoad === 'error' && (
              <p role="alert" className="text-[11px] text-rose-600 dark:text-rose-400">
                {projectLoadError ?? t.failedLoadProjects}{' '}
                <button
                  type="button"
                  onClick={() => setProjectLoadNonce((nonce) => nonce + 1)}
                  className="font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                >
                  {t.briefRetry}
                </button>
              </p>
            )}
            {projectLoad === 'empty' && (
              <p className="text-[11px] text-[var(--text-tertiary)]">{t.projectNoneHint}</p>
            )}
            {(projectLoad === 'connect' || projectLoad === 'reauth') && (
              <p className="text-[11px] text-[var(--text-tertiary)]">
                {t.projectConnectHint}{' '}
                <Link
                  to={SETTINGS_BACKLOG_PATH}
                  onClick={onClose}
                  className="font-semibold text-[var(--accent)] underline underline-offset-2"
                >
                  {t.projectOpenSettings}
                </Link>
              </p>
            )}

            {status !== 'idle' && projectLoad === 'ready' && (
              <div className="flex items-start justify-between gap-3 pt-0.5">
                <p className="text-[11px] text-[var(--text-tertiary)]">{t.briefProjectHint}</p>
                {status !== 'loading' && project && (
                  <button
                    type="button"
                    onClick={refresh}
                    className="inline-flex shrink-0 items-center gap-1 rounded text-[11px] font-medium text-[var(--accent)] transition-colors hover:text-[var(--accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                  >
                    <RotateCwIcon className="h-3.5 w-3.5" />
                    <span>{t.briefRefresh}</span>
                  </button>
                )}
              </div>
            )}
          </section>

          <div className="mt-5">
            {status === 'skipped' && (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center sm:py-14">
                <div className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-light)] text-[var(--accent)]">
                  <SparklesIcon className="h-5 w-5" />
                </div>
                <h3 className="text-xs font-bold text-[var(--text-primary)]">
                  {t.briefNoBriefableTitle}
                </h3>
                <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-[var(--text-secondary)]">
                  {t.briefNoBriefableBody}
                </p>
              </div>
            )}

            {status === 'idle' && (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center sm:py-14">
                <div className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-light)] text-[var(--accent)]">
                  <SparklesIcon className="h-5 w-5" />
                </div>
                <h3 className="text-xs font-bold text-[var(--text-primary)]">
                  {t.briefEmptyTitle}
                </h3>
                <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-[var(--text-secondary)]">
                  {t.briefEmptyBody}
                </p>
              </div>
            )}

            {status === 'loading' && !project && (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center sm:py-14">
                <div className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-light)] text-[var(--accent)]">
                  <SparklesIcon className="h-5 w-5 motion-safe:animate-pulse" />
                </div>
                <h3 className="text-xs font-bold text-[var(--text-primary)]">
                  {t.briefAiPreparing}
                </h3>
              </div>
            )}

            {status === 'loading' && project && (
              <BriefLoadingState
                projectName={project.name}
                previousMeetingDate={previousDateLabel}
                startedAt={startedAt}
              />
            )}

            {status === 'ready' && data && <BriefContent data={data} now={now} />}

            {status === 'error' && (
              <div
                role="alert"
                className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-500/20 dark:bg-rose-500/10"
              >
                <p className="text-xs font-bold text-rose-700 dark:text-rose-300">
                  {t.briefAiFailed}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-rose-600 dark:text-rose-400">
                  {t.briefErrorBody}
                </p>
                <button
                  type="button"
                  onClick={refresh}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
                >
                  <RotateCwIcon className="h-3.5 w-3.5" />
                  <span>{t.briefRetry}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2.5 border-t border-[var(--border-color)] px-5 py-3.5 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-2 text-xs font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t.close}
          </button>
          <button
            type="button"
            onClick={joinMeeting}
            className="rounded-xl bg-[#464eb8] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#3b429f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#464eb8]/40"
          >
            {joinLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  )
}
